import React, { useState, useEffect } from 'react';
import { Upload, Download, FileText, CheckCircle, XCircle, AlertCircle, ChevronRight, ChevronLeft, Settings, Play, FileSpreadsheet, ArrowUp, ArrowDown } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';
import * as XLSX from 'xlsx';

const ImportPersonnel = () => {
  const { filialeId, currentFiliale } = useFiliale();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Configuration de l'import
  const [config, setConfig] = useState({
    autoGenerateMatricule: true,
    initSoldeConge: true,
    createContrat: true,
    sendWelcomeEmail: false,
    createUser: false,
    rejectErrors: true,
    ignoreDuplicates: false,
    createMissingRefs: false,
    importEmployesSortis: false,
    importSatellites: {
      soldeConge: false,
      contrats: false,
      mouvements: false,
      planningConge: false
    },
    referenceDefaults: {
      departement_id: '',
      titre_id: '',
      type_personnel_id: '',
      type_contrat: '',
      activite_id: '',
      idcampagne: '',
      marque_id: ''
    }
  });

  // État du fichier
  const [file, setFile] = useState(null);
  const [fileData, setFileData] = useState(null);

  // Mapping des colonnes
  const [columnMapping, setColumnMapping] = useState({});

  // Validation
  const [validationResults, setValidationResults] = useState(null);

  // Import
  const [importProgress, setImportProgress] = useState(null);

  // Rapport
  const [importReport, setImportReport] = useState(null);

  // Auto-mapping effect
  useEffect(() => {
    if (fileData && Object.keys(columnMapping).length === 0) {
      const expectedFields = [
        { key: 'filiale_id', label: 'Filiale', required: true },
        { key: 'matricule', label: 'Matricule', required: false },
        { key: 'nom', label: 'Nom', required: true },
        { key: 'prenom', label: 'Prénom', required: true },
        { key: 'autres_prenom', label: 'Autres prénoms', required: false },
        { key: 'sexe', label: 'Sexe', required: true },
        { key: 'date_naissance', label: 'Date naissance', required: true },
        { key: 'lieu_naissance', label: 'Lieu naissance', required: false },
        { key: 'nationalite', label: 'Nationalité', required: true },
        { key: 'type_piece_id', label: 'Type pièce', required: true },
        { key: 'num_piece_id', label: 'Numéro pièce', required: true },
        { key: 'lieu_delivrance_piece', label: 'Lieu délivrance', required: false },
        { key: 'date_expiration_piece', label: 'Expiration pièce', required: true },
        { key: 'numtel', label: 'Téléphone', required: false },
        { key: 'adressemail', label: 'Email', required: false },
        { key: 'adresse_complete', label: 'Adresse', required: false },
        { key: 'personne_contact', label: 'Personne contact', required: false },
        { key: 'numero_cnss', label: 'CNSS', required: false },
        { key: 'situation_matrimoniale', label: 'Situation mat.', required: false },
        { key: 'nombre_enfant_charge', label: 'Enfants charge', required: false },
        { key: 'direct_indirect', label: 'Direct/Indirect', required: true },
        { key: 'date_entree', label: 'Date entrée', required: true },
        { key: 'date_sortie', label: 'Date sortie', required: false },
        { key: 'motif_sortie_id', label: 'Motif sortie', required: false },
        { key: 'departement_id', label: 'Département', required: true },
        { key: 'titre_id', label: 'Titre', required: true },
        { key: 'statut', label: 'Statut', required: true },
        { key: 'type_contrat', label: 'Type contrat', required: true },
        { key: 'type_personnel_id', label: 'Type personnel', required: true },
        { key: 'activite_id', label: 'Activité', required: false },
        { key: 'idcampagne', label: 'Campagne', required: false },
        { key: 'marque_id', label: 'Marque', required: false },
        { key: 'emplacement', label: 'Emplacement', required: true },
        { key: 'manager_hierarchique_id', label: 'Manager', required: false },
        { key: 'fonction_manager', label: 'Fonction manager', required: false },
        { key: 'n2_id', label: 'N+2', required: false },
        { key: 'domaine_etude', label: 'Domaine étude', required: false },
        { key: 'niveau_etude', label: 'Niveau étude', required: false },
        { key: 'domiciliation', label: 'Domiciliation', required: false },
        { key: 'numero_ifu', label: 'IFU', required: false },
        { key: 'grille_personnel_id', label: 'Grille', required: false },
        { key: 'solde_conge', label: 'Solde congé', required: false }
      ];

      const newMapping = {};
      expectedFields.forEach(field => {
        const normalizedField = field.label.toLowerCase().replace(/\s+/g, '').replace(/\*/g, '');
        const matchedHeader = fileData.headers.find(header => {
          const normalizedHeader = header.toLowerCase().replace(/\s+/g, '').replace(/\*/g, '');
          return normalizedHeader === normalizedField || normalizedHeader.startsWith(normalizedField);
        });
        if (matchedHeader) {
          newMapping[field.key] = matchedHeader;
        }
      });
      setColumnMapping(newMapping);
    }
  }, [fileData]);

  // Validation effect
  useEffect(() => {
    if (currentStep === 5 && !validationResults && fileData) {
      const validateData = async () => {
        setLoading(true);
        const errors = [];
        const warnings = [];
        const validRows = [];

        try {
          console.log('[IMPORT VALIDATE] Envoi des données au backend');
          console.log('[IMPORT VALIDATE] fileData.rows:', fileData.rows);
          console.log('[IMPORT VALIDATE] columnMapping:', columnMapping);
          console.log('[IMPORT VALIDATE] config:', config);
          console.log('[IMPORT VALIDATE] filialeId:', filialeId);

          const response = await http.post('/api/import-personnel/validate', {
            data: fileData.rows,
            mapping: columnMapping,
            config: config,
            filiale_id: filialeId,
            importEmployesSortis: config.importEmployesSortis
          });

          const validationResults = response.data;
          setValidationResults(validationResults);
        } catch (error) {
          console.error('[IMPORT] Erreur validation:', error);
          errors.push({ message: 'Erreur lors de la validation: ' + (error.message || 'Erreur inconnue') });
          setValidationResults({ errors, warnings, validRows: [], total: fileData.totalRows });
        } finally {
          setLoading(false);
        }
      };
      validateData();
    }
  }, [currentStep, fileData, columnMapping, config, filialeId]);

  // Import effect
  useEffect(() => {
    if (currentStep === 6 && !importReport && validationResults) {
      const executeImport = async () => {
        if (!validationResults || validationResults.validRows.length === 0) return;

        setLoading(true);
        setImportProgress({ current: 0, total: validationResults.validRows.length, status: 'Importation en cours...' });

        try {
          const response = await http.post('/api/import-personnel/import', {
            data: validationResults.validRows,
            mapping: columnMapping,
            config: config,
            filiale_id: filialeId,
            importEmployesSortis: config.importEmployesSortis
          });

          setImportReport(response.data);
          setImportProgress({ current: response.data.total, total: response.data.total, status: 'Terminé' });
        } catch (error) {
          console.error('[IMPORT] Erreur import:', error);
          setImportReport({
            success: 0,
            errors: [{ message: 'Erreur lors de l\'import: ' + (error.message || 'Erreur inconnue') }],
            total: validationResults.validRows.length
          });
        } finally {
          setLoading(false);
        }
      };
      executeImport();
    }
  }, [currentStep, validationResults, columnMapping, config, filialeId]);

  // Dropdown data
  const [dropdownData, setDropdownData] = useState({
    departements: [],
    titres: [],
    typesPersonnel: [],
    typesContrat: [],
    activites: [],
    campagnes: [],
    marques: []
  });

  useEffect(() => {
    fetchDropdownData();
  }, [filialeId]);

  const fetchDropdownData = async () => {
    try {
      const [deps, titles, typesPers, contrats, acts, camps, marques] = await Promise.all([
        http.get('/api/departements'),
        http.get('/api/titres' + (filialeId ? `?filiale_id=${filialeId}` : '')),
        http.get('/api/types-personnel' + (filialeId ? `?filiale_id=${filialeId}` : '')),
        http.get('/api/types-contrat'),
        http.get('/api/activites'),
        http.get('/api/campagnes'),
        http.get('/api/marques')
      ]);
      setDropdownData({
        departements: deps.data || [],
        titres: titles.data || [],
        typesPersonnel: typesPers.data || [],
        typesContrat: contrats.data || [],
        activites: acts.data || [],
        campagnes: camps.data || [],
        marques: marques.data || []
      });
    } catch (error) {
      console.error('Erreur chargement données:', error);
    }
  };

  const handleNext = () => {
    if (currentStep < 7) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return renderConfiguration();
      case 2:
        return renderTemplate();
      case 3:
        return renderUpload();
      case 4:
        return renderMapping();
      case 5:
        return renderValidation();
      case 6:
        return renderImport();
      case 7:
        return renderReport();
      default:
        return null;
    }
  };

  const renderConfiguration = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">🔧 Paramètres Généraux</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Filiale de destination</label>
            <select
              value={filialeId || ''}
              disabled
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
            >
              <option value="">Sélectionner une filiale</option>
              {currentFiliale && (
                <option value={filialeId}>{currentFiliale.libelle} ({currentFiliale.code})</option>
              )}
            </select>
            <p className="text-xs text-gray-500 mt-1">Héritée du contexte filiale</p>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">📊 Gestion du Matricule</h3>
        <div className="space-y-3">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={config.autoGenerateMatricule}
              onChange={(e) => setConfig({ ...config, autoGenerateMatricule: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm text-gray-700">Générer automatiquement les matricules selon la nomenclature</span>
          </label>
          {!config.autoGenerateMatricule && (
            <p className="text-xs text-gray-500 ml-6">Les matricules du fichier Excel seront utilisés</p>
          )}
        </div>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-green-900 mb-2">✅ Déclenchements Automatiques</h3>
        <div className="space-y-2">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={config.initSoldeConge}
              onChange={(e) => setConfig({ ...config, initSoldeConge: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm text-gray-700">Initialiser le solde de congé (par défaut: 30 jours)</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={config.createContrat}
              onChange={(e) => setConfig({ ...config, createContrat: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm text-gray-700">Créer l'enregistrement de contrat</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={config.sendWelcomeEmail}
              onChange={(e) => setConfig({ ...config, sendWelcomeEmail: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm text-gray-700">Envoyer un email de bienvenue</span>
          </label>
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-yellow-900 mb-2">⚠️ Options de Validation</h3>
        <div className="space-y-2">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={config.rejectErrors}
              onChange={(e) => setConfig({ ...config, rejectErrors: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm text-gray-700">Rejeter les lignes avec erreurs (recommandé)</span>
          </label>
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={config.createMissingRefs}
              onChange={(e) => setConfig({ ...config, createMissingRefs: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm text-gray-700">Créer automatiquement les références manquantes</span>
          </label>
        </div>
      </div>

      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-red-900 mb-2">👋 Import d'Employés Sortis</h3>
        <div className="space-y-2">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={config.importEmployesSortis}
              onChange={(e) => setConfig({ ...config, importEmployesSortis: e.target.checked })}
              className="rounded"
            />
            <span className="text-sm text-gray-700">Importer des employés sortis (active = 0)</span>
          </label>
          {config.importEmployesSortis && (
            <p className="text-xs text-gray-500 ml-6">Si activé, les colonnes "Date sortie" et "Motif sortie" seront obligatoires</p>
          )}
        </div>
      </div>
    </div>
  );

  const renderTemplate = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
        <FileSpreadsheet className="h-16 w-16 text-blue-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-blue-900 mb-2">Template d'Import</h3>
        <p className="text-sm text-gray-600 mb-4">
          Téléchargez le template Excel pré-rempli avec les données de référence
        </p>
        <button
          onClick={() => handleDownloadTemplate()}
          className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Download className="h-5 w-5 mr-2" />
          Télécharger Template (.xlsx)
        </button>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">📋 Instructions de remplissage</h3>
        <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
          <li>Téléchargez le template Excel</li>
          <li>Remplissez les données (respectez les formats)</li>
          <li>Les colonnes avec * sont obligatoires</li>
          <li>Utilisez les listes déroulantes pour les champs de référence</li>
          <li>Sauvegardez le fichier</li>
          <li>Passez à l'étape suivante pour l'upload</li>
        </ol>
      </div>
    </div>
  );

  const renderUpload = () => (
    <div className="space-y-6">
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
        <Upload className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <p className="text-lg text-gray-600 mb-2">Glissez votre fichier ici ou</p>
        <label className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer">
          <FileText className="h-5 w-5 mr-2" />
          Parcourir les fichiers
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileUpload}
            className="hidden"
          />
        </label>
        <p className="text-sm text-gray-500 mt-2">Formats acceptés: .xlsx, .xls, .csv</p>
      </div>

      {file && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FileText className="h-8 w-8 text-green-600 mr-3" />
              <div>
                <p className="font-medium text-gray-900">{file.name}</p>
                <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
              </div>
            </div>
            <button
              onClick={() => setFile(null)}
              className="text-red-600 hover:text-red-800"
            >
              Supprimer
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderMapping = () => {
    const expectedFields = [
      { key: 'filiale_id', label: 'Filiale', required: true },
      { key: 'matricule', label: 'Matricule', required: false },
      { key: 'nom', label: 'Nom', required: true },
      { key: 'prenom', label: 'Prénom', required: true },
      { key: 'autres_prenom', label: 'Autres prénoms', required: false },
      { key: 'sexe', label: 'Sexe', required: true },
      { key: 'date_naissance', label: 'Date naissance', required: true },
      { key: 'nationalite', label: 'Nationalité', required: true },
      { key: 'type_piece_id', label: 'Type pièce', required: true },
      { key: 'num_piece_id', label: 'Numéro pièce', required: true },
      { key: 'lieu_delivrance_piece', label: 'Lieu délivrance', required: false },
      { key: 'date_expiration_piece', label: 'Expiration pièce', required: true },
      { key: 'numtel', label: 'Téléphone', required: false },
      { key: 'adressemail', label: 'Email', required: false },
      { key: 'adresse_complete', label: 'Adresse', required: false },
      { key: 'personne_contact', label: 'Personne contact', required: false },
      { key: 'numero_cnss', label: 'CNSS', required: false },
      { key: 'situation_matrimoniale', label: 'Situation mat.', required: false },
      { key: 'nombre_enfant_charge', label: 'Enfants charge', required: false },
      { key: 'direct_indirect', label: 'Direct/Indirect', required: true },
      { key: 'date_entree', label: 'Date entrée', required: true },
      { key: 'date_sortie', label: 'Date sortie', required: config.importEmployesSortis },
      { key: 'motif_sortie_id', label: 'Motif sortie', required: config.importEmployesSortis },
      { key: 'departement_id', label: 'Département', required: true },
      { key: 'titre_id', label: 'Titre', required: true },
      { key: 'statut', label: 'Statut', required: true },
      { key: 'type_contrat', label: 'Type contrat', required: true },
      { key: 'type_personnel_id', label: 'Type personnel', required: true },
      { key: 'activite_id', label: 'Activité', required: false },
      { key: 'idcampagne', label: 'Campagne', required: false },
      { key: 'marque_id', label: 'Marque', required: false },
      { key: 'emplacement', label: 'Emplacement', required: true },
      { key: 'manager_hierarchique_id', label: 'Manager', required: false },
      { key: 'fonction_manager', label: 'Fonction manager', required: false },
      { key: 'n2_id', label: 'N+2', required: false },
      { key: 'domaine_etude', label: 'Domaine étude', required: false },
      { key: 'niveau_etude', label: 'Niveau étude', required: false },
      { key: 'domiciliation', label: 'Domiciliation', required: false },
      { key: 'numero_ifu', label: 'IFU', required: false },
      { key: 'grille_personnel_id', label: 'Grille', required: false },
      { key: 'solde_conge', label: 'Solde congé', required: false }
    ];

    const autoMapColumns = () => {
      const newMapping = {};
      expectedFields.forEach(field => {
        const normalizedField = field.label.toLowerCase().replace(/\s+/g, '').replace(/\*/g, '');
        const matchedHeader = fileData.headers.find(header => {
          const normalizedHeader = header.toLowerCase().replace(/\s+/g, '').replace(/\*/g, '');
          return normalizedHeader === normalizedField || normalizedHeader.startsWith(normalizedField);
        });
        if (matchedHeader) {
          newMapping[field.key] = matchedHeader;
        }
      });
      setColumnMapping(newMapping);
    };

    return (
      <div className="space-y-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-yellow-900 mb-2">🔗 Mapping des Colonnes</h3>
          <p className="text-sm text-gray-700">
            Associez les colonnes du fichier Excel aux champs de la base de données
          </p>
          <button
            onClick={autoMapColumns}
            className="mt-2 px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-sm"
          >
            🔄 Auto-mapping intelligent
          </button>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Colonne Excel</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Champ BDD</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {expectedFields.map(field => {
                const mappedColumn = columnMapping[field.key];
                const isMapped = !!mappedColumn;
                const isRequired = field.required;

                return (
                  <tr key={field.key} className={isRequired && !isMapped ? 'bg-red-50' : ''}>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {isMapped ? mappedColumn : <span className="text-gray-400">Non mappé</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {field.label} {isRequired && <span className="text-red-500">*</span>}
                    </td>
                    <td className="px-4 py-3">
                      {isMapped ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : isRequired ? (
                        <XCircle className="h-5 w-5 text-red-600" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-yellow-600" />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 mb-2">📊 Statistiques du mapping</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Colonnes mappées: </span>
              <span className="font-bold text-green-600">{Object.keys(columnMapping).length}</span>
            </div>
            <div>
              <span className="text-gray-600">Obligatoires: </span>
              <span className="font-bold">{expectedFields.filter(f => f.required).length}</span>
            </div>
            <div>
              <span className="text-gray-600">Lignes de données: </span>
              <span className="font-bold">{fileData?.totalRows || 0}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderValidation = () => {
    if (loading) {
      return (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-blue-900">Validation en cours...</p>
          </div>
        </div>
      );
    }

    if (!validationResults) {
      return (
        <div className="space-y-6">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-gray-600">Validation en cours...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">📊 Résumé de la Validation</h3>
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div className="bg-green-50 border border-green-200 rounded p-3">
              <p className="text-sm text-gray-600">Valides</p>
              <p className="text-2xl font-bold text-green-600">{validationResults.validRows?.length || 0}</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
              <p className="text-sm text-gray-600">Avertissements</p>
              <p className="text-2xl font-bold text-yellow-600">{validationResults.warnings?.length || 0}</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded p-3">
              <p className="text-sm text-gray-600">Erreurs</p>
              <p className="text-2xl font-bold text-red-600">{validationResults.errors?.length || 0}</p>
            </div>
          </div>
        </div>

        {validationResults.errors && validationResults.errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-semibold text-red-900 mb-2">❌ Erreurs</h4>
            <ul className="space-y-1 text-sm max-h-60 overflow-y-auto">
              {validationResults.errors.map((error, idx) => (
                <li key={idx} className="text-red-700">
                  Ligne {error.row}: {error.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        {validationResults.warnings && validationResults.warnings.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-900 mb-2">⚠️ Avertissements</h4>
            <ul className="space-y-1 text-sm max-h-60 overflow-y-auto">
              {validationResults.warnings.map((warning, idx) => (
                <li key={idx} className="text-yellow-700">
                  Ligne {warning.row}: {warning.message}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderImport = () => {
    if (loading && !importReport) {
      return (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">🚀 Importation en Cours</h3>
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div
                  className="bg-blue-600 h-4 rounded-full transition-all duration-300"
                  style={{ width: `${((importProgress?.current || 0) / (importProgress?.total || 1)) * 100}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {importProgress?.current || 0} / {importProgress?.total || 0} employés traités
              </p>
              <p className="text-sm text-gray-500">{importProgress?.status}</p>
            </div>
          </div>
        </div>
      );
    }

    if (!importReport) {
      return (
        <div className="space-y-6">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-gray-600">Importation en cours...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-green-900 mb-2">✅ Import Terminé</h3>
          <p className="text-sm text-gray-600">L'importation est terminée. Passez à l'étape suivante pour voir le rapport.</p>
        </div>
      </div>
    );
  };

  const renderReport = () => {
    if (!importReport) {
      return (
        <div className="space-y-6">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-gray-600">Aucun rapport disponible</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-green-900 mb-2">📊 Rapport d'Import</h3>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <p className="text-sm text-gray-600">Total traité: </p>
              <p className="text-2xl font-bold text-gray-900">{importReport.total || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Succès: </p>
              <p className="text-2xl font-bold text-green-600">{importReport.success || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Erreurs: </p>
              <p className="text-2xl font-bold text-red-600">{importReport.errors?.length || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Temps: </p>
              <p className="text-2xl font-bold text-gray-900">{importReport.duration || 'N/A'}</p>
            </div>
          </div>
        </div>

        {importReport.errors && importReport.errors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-semibold text-red-900 mb-2">❌ Erreurs d'import</h4>
            <ul className="space-y-1 text-sm max-h-60 overflow-y-auto">
              {importReport.errors.map((error, idx) => (
                <li key={idx} className="text-red-700">
                  {error.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex space-x-4">
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Importer un autre fichier
          </button>
          <button
            onClick={() => window.location.href = '/personnel'}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Voir les employés importés
          </button>
        </div>
      </div>
    );
  };

  const handleDownloadTemplate = async () => {
    try {
      console.log('[IMPORT TEMPLATE FRONT] Début téléchargement template');
      console.log('[IMPORT TEMPLATE FRONT] filialeId:', filialeId);
      console.log('[IMPORT TEMPLATE FRONT] currentFiliale:', currentFiliale);
      console.log('[IMPORT TEMPLATE FRONT] config:', config);

      const params = {
        ...(filialeId ? { filiale_id: filialeId } : {}),
        initSoldeConge: config.initSoldeConge,
        autoGenerateMatricule: config.autoGenerateMatricule
      };
      console.log('[IMPORT TEMPLATE FRONT] Params de la requête:', params);

      const response = await http.get('/api/import-personnel/template', {
        responseType: 'blob',
        params
      });

      console.log('[IMPORT TEMPLATE FRONT] Réponse reçue, status:', response.status);
      console.log('[IMPORT TEMPLATE FRONT] Type de réponse:', response.headers['content-type']);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `template_import_employes_${currentFiliale?.code || 'all'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      console.log('[IMPORT TEMPLATE FRONT] Template téléchargé avec succès');
    } catch (error) {
      console.error('[IMPORT TEMPLATE FRONT] Erreur téléchargement template:', error);
      console.error('[IMPORT TEMPLATE FRONT] Détails de l\'erreur:', error.response);
      alert('Erreur lors du téléchargement du template: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const handleFileUpload = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);

      // Parser le fichier Excel
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });

          // Chercher la feuille "Import Employés" ou une feuille contenant "Import"
          let sheetName = workbook.SheetNames[0];
          for (const name of workbook.SheetNames) {
            if (name.includes('Import') || name.includes('Employés')) {
              sheetName = name;
              break;
            }
          }

          console.log('[IMPORT] Feuille utilisée:', sheetName);
          console.log('[IMPORT] Feuilles disponibles:', workbook.SheetNames);

          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          console.log('[IMPORT] Données Excel brutes:', jsonData);

          if (jsonData.length > 0) {
            const headers = jsonData[0];
            const rawRows = jsonData.slice(1);

            // Convertir les lignes en tableau d'objets
            const rows = rawRows.map(row => {
              const obj = {};
              headers.forEach((header, index) => {
                obj[header] = row[index];
              });
              return obj;
            });

            setFileData({
              headers,
              rows,
              totalRows: rows.length
            });

            console.log('[IMPORT] Colonnes détectées:', headers);
            console.log('[IMPORT] Nombre de lignes:', rows.length);
            console.log('[IMPORT] Première ligne (objet):', rows[0]);
          }
        } catch (error) {
          console.error('[IMPORT] Erreur parsing Excel:', error);
          alert('Erreur lors de la lecture du fichier Excel');
        }
      };
      reader.readAsArrayBuffer(selectedFile);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Importation des Employés"
        description="Importez des employés depuis un fichier Excel"
        icon={Upload}
        actions={[
          {
            label: 'Template',
            icon: Download,
            onClick: () => setCurrentStep(2),
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8">
        {/* Filiale context */}
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <span className="text-sm text-gray-600">Filiale: </span>
          <span className="font-medium text-gray-900 ml-2">
            {currentFiliale ? `${currentFiliale.libelle} (${currentFiliale.code})` : 'Non sélectionnée'}
          </span>
        </div>

        {/* Wizard Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {[1, 2, 3, 4, 5, 6, 7].map((step) => (
              <div key={step} className="flex items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                    step <= currentStep
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {step}
                </div>
                {step < 7 && (
                  <div
                    className={`w-16 h-1 ${
                      step < currentStep ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>Configuration</span>
            <span>Template</span>
            <span>Upload</span>
            <span>Mapping</span>
            <span>Validation</span>
            <span>Import</span>
            <span>Rapport</span>
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {renderStep()}
        </div>

        {/* Navigation */}
        <div className="mt-6 flex justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-5 w-5 inline mr-2" />
            Précédent
          </button>
          <button
            onClick={handleNext}
            disabled={currentStep === 7 || (currentStep === 3 && !file)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {currentStep === 7 ? 'Terminer' : 'Suivant'}
            <ChevronRight className="h-5 w-5 inline ml-2" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportPersonnel;
